import {Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle} from "@mui/material";
import {useLocalization} from "@smalox/shared";
import {useMutation} from "@tanstack/react-query";
import {ApiError, ErrorAlert, TextField, useFlag} from "@variocube/app-ui";
import React, {Fragment, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useTenantUserApi, useUserApi} from "../../api";

function isNotFound(e: unknown) {
	return e instanceof ApiError && e.status === 404;
}

export function UserCreateButton() {
	const {t} = useLocalization();
	const {findUserByEmail} = useUserApi();
	const {getUser, addExistingUser} = useTenantUserApi();
	const navigate = useNavigate();

	const [open, setOpen, clearOpen] = useFlag(false);
	const [email, setEmail] = useState("");
	// the username of an existing user that is not yet a member of this tenant, awaiting confirmation to add
	const [pendingUsername, setPendingUsername] = useState<string>();

	const lookup = useMutation(async () => {
		const trimmedEmail = email.trim();

		// look up an existing user by email
		let username: string;
		try {
			username = (await findUserByEmail(trimmedEmail)).username;
		}
		catch (lookupError) {
			if (isNotFound(lookupError)) {
				// no such user yet → create a new one with the email prefilled
				navigate("new", {state: {email: trimmedEmail}});
				return;
			}
			throw lookupError;
		}

		// the user exists globally; check whether they are already a member of this tenant
		try {
			await getUser(username);
			// already a member → go to the user
			navigate(username);
		}
		catch (membershipError) {
			if (isNotFound(membershipError)) {
				// exists in another tenant → ask for confirmation before binding
				setPendingUsername(username);
				return;
			}
			throw membershipError;
		}
	});

	const bind = useMutation(async (username: string) => {
		await addExistingUser(username);
		navigate(username, {state: {addedFromOtherTenant: true}});
	});

	function handleClose() {
		setEmail("");
		setPendingUsername(undefined);
		lookup.reset();
		bind.reset();
		clearOpen();
	}

	function handleLookup(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		lookup.mutate();
	}

	const error = lookup.error ?? bind.error;

	return (
		<Fragment>
			<Button variant="contained" onClick={setOpen}>
				{t("users.create.title")}
			</Button>

			<Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
				{!pendingUsername
					? (
						<form onSubmit={handleLookup}>
							<DialogTitle>{t("users.create.emailPrompt.title")}</DialogTitle>
							<DialogContent>
								<DialogContentText>{t("users.create.emailPrompt.description")}</DialogContentText>
								<Box pt={2}>
									<TextField
										label={t("users.email")}
										value={email}
										onChange={setEmail}
										type="email"
										fullWidth
										required
										autoFocus
									/>
								</Box>
								{error && (
									<Box pt={2}>
										<ErrorAlert error={error} />
									</Box>
								)}
							</DialogContent>
							<DialogActions>
								<Button color="inherit" onClick={handleClose}>
									{t("cancel")}
								</Button>
								<Button type="submit" variant="contained" disabled={lookup.isLoading || !email}>
									{t("next")}
								</Button>
							</DialogActions>
						</form>
					)
					: (
						<Fragment>
							<DialogTitle>{t("users.create.addExisting.title")}</DialogTitle>
							<DialogContent>
								<DialogContentText>
									{t("users.create.addExisting.description", {email})}
								</DialogContentText>
								{error && (
									<Box pt={2}>
										<ErrorAlert error={error} />
									</Box>
								)}
							</DialogContent>
							<DialogActions>
								<Button color="inherit" onClick={handleClose}>
									{t("cancel")}
								</Button>
								<Button
									variant="contained"
									onClick={() => bind.mutate(pendingUsername)}
									disabled={bind.isLoading}
								>
									{t("users.create.addExisting.confirm")}
								</Button>
							</DialogActions>
						</Fragment>
					)}
			</Dialog>
		</Fragment>
	);
}
